import React from "react";
import { Button, Select, Tooltip } from "antd";
import { ClearOutlined } from "@ant-design/icons";

import api from "@/api/axios.js";

export const EMPTY_FLYER_FILTERS = { cooperativaIds: [], canaleIds: [], promoIds: [] };

const byLabel = (a, b) => String(a.label).localeCompare(String(b.label), "it");

/**
 * Aggiunge il nome del genitore all'etichetta quando la lista abbraccia più
 * cooperative. Serve davvero: in produzione ci sono 7 canali chiamati
 * "Supermercati" e 3 "Superstore" sotto clienti diversi, e senza il suffisso
 * la tenda mostrerebbe voci identiche e indistinguibili.
 */
function decorateOptions(items) {
	const parents = new Set(items.map(item => item.parentKey).filter(Boolean));
	const showParent = parents.size > 1;

	return items
		.map(item => ({
			value: item.value,
			label: showParent && item.parentLabel ? `${item.label} · ${item.parentLabel}` : item.label
		}))
		.sort(byLabel);
}

/**
 * Barra filtri della pagina Volantini: cliente (COOPERATIVA), canale e promo.
 *
 * Componente controllato: lo stato dei filtri vive nella URL della pagina, qui
 * restano solo le opzioni, caricate una volta dall'anagrafica e non dedotte dai
 * volantini caricati. Una cooperativa senza volantini resta quindi selezionabile
 * e produce un risultato vuoto esplicito, più chiaro di un'opzione che sparisce.
 */
export default function FlyerFiltersBar({ value, onChange, disabled = false }) {
	const [orgNodes, setOrgNodes] = React.useState([]);
	const [promos, setPromos] = React.useState([]);
	const [loading, setLoading] = React.useState(true);
	const [failed, setFailed] = React.useState(false);

	React.useEffect(() => {
		let cancelled = false;

		(async () => {
			try {
				const [orgRes, promoRes] = await Promise.all([
					api.get("/api/orgnodes"),
					api.get("/api/promos", { params: { limit: 500 } })
				]);
				if (cancelled) return;
				setOrgNodes(Array.isArray(orgRes.data?.items) ? orgRes.data.items : []);
				setPromos(Array.isArray(promoRes.data?.items) ? promoRes.data.items : []);
				setFailed(false);
			}
			catch {
				if (cancelled) return;
				setOrgNodes([]);
				setPromos([]);
				setFailed(true);
			}
			finally {
				if (!cancelled) setLoading(false);
			}
		})();

		return () => { cancelled = true; };
	}, []);

	const cooperativeById = React.useMemo(() => {
		const map = new Map();
		orgNodes.filter(n => n.type === "COOPERATIVA").forEach(n => map.set(String(n._id), n.name || "—"));
		return map;
	}, [orgNodes]);

	const cooperativaOptions = React.useMemo(
		() => [...cooperativeById.entries()].map(([v, label]) => ({ value: v, label })).sort(byLabel),
		[cooperativeById]
	);

	const selectedClients = React.useMemo(() => new Set(value.cooperativaIds.map(String)), [value.cooperativaIds]);

	const canaleOptions = React.useMemo(() => decorateOptions(
		orgNodes
			.filter(n => n.type === "CANALE")
			.filter(n => !selectedClients.size || selectedClients.has(String(n.parentId)))
			.map(n => ({
				value: String(n._id),
				label: n.name || "—",
				parentKey: String(n.parentId || ""),
				parentLabel: cooperativeById.get(String(n.parentId)) || ""
			}))
	), [orgNodes, selectedClients, cooperativeById]);

	const promoOptions = React.useMemo(() => decorateOptions(
		promos
			.filter(p => !selectedClients.size || selectedClients.has(String(p.cooperativaId)))
			.map(p => ({
				value: String(p._id || p.id),
				label: p.name || "—",
				parentKey: String(p.cooperativaId || ""),
				parentLabel: p.cooperativaName || ""
			}))
	), [promos, selectedClients]);

	const handleClientsChange = (next) => {
		const selected = new Set(next.map(String));
		const canaleParent = new Map(
			orgNodes.filter(n => n.type === "CANALE").map(n => [String(n._id), String(n.parentId || "")])
		);
		const promoParent = new Map(promos.map(p => [String(p._id || p.id), String(p.cooperativaId || "")]));
		const belongs = (map) => (id) => !selected.size || selected.has(map.get(String(id)) || "");

		// Potatura: le selezioni di canale e promo non più pertinenti al cliente
		// scelto vengono tolte subito. È la conseguenza diretta di un'azione
		// dell'utente, quindi visibile; NON avviene mai al caricamento della
		// pagina, dove un link incoerente va rispettato alla lettera.
		onChange({
			cooperativaIds: next.map(String),
			canaleIds: value.canaleIds.filter(belongs(canaleParent)),
			promoIds: value.promoIds.filter(belongs(promoParent))
		});
	};

	const hasFilters = value.cooperativaIds.length > 0 || value.canaleIds.length > 0 || value.promoIds.length > 0;
	const commonProps = {
		mode: "multiple",
		allowClear: true,
		showSearch: true,
		optionFilterProp: "label",
		maxTagCount: "responsive",
		loading,
		disabled: disabled || failed
	};

	const bar = (
		<div className="vp-filters">
			<Select
				{...commonProps}
				style={{ minWidth: 220 }}
				placeholder="Cliente"
				value={value.cooperativaIds}
				onChange={handleClientsChange}
				options={cooperativaOptions}
			/>
			<Select
				{...commonProps}
				style={{ minWidth: 220 }}
				placeholder="Canale"
				value={value.canaleIds}
				onChange={(next) => onChange({ ...value, canaleIds: next.map(String) })}
				options={canaleOptions}
			/>
			<Select
				{...commonProps}
				style={{ minWidth: 260 }}
				placeholder="Promo"
				value={value.promoIds}
				onChange={(next) => onChange({ ...value, promoIds: next.map(String) })}
				options={promoOptions}
			/>
			{hasFilters && (
				<Button icon={<ClearOutlined />} onClick={() => onChange(EMPTY_FLYER_FILTERS)}>
					Azzera filtri
				</Button>
			)}
		</div>
	);

	if (!failed)
		return bar;

	return (
		<Tooltip title="Anagrafica non raggiungibile: i filtri sono disponibili al prossimo ricaricamento della pagina">
			{bar}
		</Tooltip>
	);
}
